import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { catchError, Subscription, switchMap, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoadingComponent } from '../../../component/loading/loading.component';
import { UserTagSelectorComponent } from '../../../component/user-tag-selector/user-tag-selector.component';
import { extForm, ExtFormComponent } from '../../../form/ext/ext.component';
import { HasChanges } from '../../../guard/pending-changes.guard';
import { AccountService } from '../../../service/account.service';
import { AdminService } from '../../../service/admin.service';
import { ExtService } from '../../../service/api/ext.service';
import { ConfigService } from '../../../service/config.service';
import { Store } from '../../../store/store';
import { controlState, scrollToFirstInvalid } from '../../../util/form';
import { printError } from '../../../util/http';

@Component({
  selector: 'app-settings-me-page',
  templateUrl: './me.component.html',
  styleUrls: ['./me.component.scss'],
  host: { 'class': 'full-page-form' },
  imports: [ReactiveFormsModule, UserTagSelectorComponent, ExtFormComponent, LoadingComponent]
})
export class SettingsMePage implements HasChanges {
  config = inject(ConfigService);
  store = inject(Store);
  private exts = inject(ExtService);
  private accounts = inject(AccountService);
  private admin = inject(AdminService);
  private fb = inject(FormBuilder);
  private location = inject(Location);


  readonly submitted = signal<boolean>(false);
  readonly serverError = signal<string[]>([]);
  readonly editing = signal(false);
  private editingSubscription?: Subscription;

  readonly ext = cloneDeep(this.store.account.ext());
  editForm!: UntypedFormGroup;
  protected readonly editFormValid = controlState(() => this.editForm, c => c.valid);

  constructor() {
    const fb = this.fb;

    this.editForm = extForm(fb, this.ext, this.admin, true);
    this.editForm.patchValue(this.ext || {});
  }

  saveChanges() {
    return !this.editForm?.dirty;
  }

  save() {
    this.serverError.set([]);
    this.submitted.set(true);
    this.editForm.markAllAsTouched();
    if (!this.editForm.valid) {
      scrollToFirstInvalid();
      return;
    }
    const ext = this.store.account.ext()!;
    this.editing.set(true);
    this.editingSubscription = this.exts.update({
      ...ext,
      ...this.editForm.value,
      tag: ext.tag, // Need to fetch because control is disabled
      config: {
        ...ext.config,
        ...this.editForm.value.config,
      },
    }).pipe(
      tap(() => this.accounts.clearCache()),
      switchMap(() => this.accounts.initExt$),
      catchError((res: HttpErrorResponse) => {
        this.editing.set(false);
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    ).subscribe(() => {
      this.editing.set(false);
      this.editForm.markAsPristine();
      this.location.back();
    });
    this.editingSubscription?.add(() => this.editing.set(false));
  }

  protected readonly isDevMode = () => environment.dev;
}
